import './styles/globals.scss';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { App as AntApp, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { installErrorForwarding } from './features/client-logs';
import { queryClient } from './lib/query-client';
import { routeTree } from './routeTree.gen';
import { useOrgConfig } from './state/use-org-config';
import { ThemeMode, useThemePreference } from './state/use-theme-preference';
import { buildTheme } from './theme/tokens';

dayjs.extend(relativeTime);

// Production only: in dev, React's own console warnings would flood the API logs.
if (import.meta.env.PROD) installErrorForwarding();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const App = () => {
  const primaryColor = useOrgConfig((s) => s.config.primaryColor);
  const mode = useThemePreference((s) => s.mode);
  const isDark = mode === ThemeMode.Dark;

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  return (
    <ConfigProvider theme={buildTheme(primaryColor ? { colorPrimary: primaryColor } : undefined, isDark)}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
};

const rootEl = document.getElementById('app');
if (!rootEl) {
  throw new Error('Missing #app root element');
}

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
